'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronLeft, Leaf, Phone, Globe } from 'lucide-react';

const CRISIS_LINES = [
  { country: '🇺🇸 USA', name: '988 Suicide & Crisis Lifeline', contact: 'Call or text 988', href: 'tel:988' },
  { country: '🇬🇧 UK', name: 'Samaritans', contact: 'Call 116 123', href: 'tel:116123' },
  { country: '🇦🇺 Australia', name: 'Lifeline', contact: 'Call 13 11 14', href: 'tel:131114' },
  { country: '🇨🇦 Canada', name: 'Crisis Services Canada', contact: 'Call 1-833-456-4566', href: 'tel:18334564566' },
  { country: '🌍 International', name: 'Find A Helpline', contact: 'findahelpline.com', href: 'https://findahelpline.com' },
];

export default function SafetyPage() {
  return (
    <div className="min-h-screen gradient-bg">
      <div className="fixed -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, #A8C5AD, transparent)' }} />
      <div className="max-w-2xl mx-auto px-5 py-8 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Link
            href="/profile"
            className="w-9 h-9 rounded-xl bg-white/80 border border-border flex items-center justify-center shadow-card-sm hover:bg-white transition-colors"
          >
            <ChevronLeft size={18} className="text-foreground" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl lavender-gradient flex items-center justify-center shadow-card-sm">
              <Leaf size={14} className="text-white" />
            </div>
            <h1 className="text-lg font-extrabold text-foreground tracking-tight">Safety & Crisis Resources</h1>
          </div>
        </div>

        {/* Emergency banner */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
          <p className="text-sm font-bold text-red-600 mb-1">🚨 In immediate danger?</p>
          <p className="text-sm text-red-700 leading-relaxed">
            If you or someone else is in immediate danger, call your local emergency services (911, 999, 000) right now. Do not wait.
          </p>
        </div>

        <div className="bg-white/80 rounded-2xl border border-border shadow-card-sm p-6 space-y-6">
          <div>
            <h2 className="text-base font-extrabold text-foreground mb-2">About Clingy & Safety</h2>
            <p className="text-sm text-foreground leading-relaxed">
              Clingy is a supportive companion, not a crisis service or licensed therapist. If you're going through something serious, please reach out to a real person — a friend, family member, or professional.
            </p>
            <p className="text-sm text-foreground leading-relaxed mt-2">
              You deserve real human support. Clingy is here to complement that, not replace it.
            </p>
          </div>

          <div>
            <h2 className="text-base font-extrabold text-foreground mb-3">Crisis Helplines</h2>
            <div className="space-y-3">
              {CRISIS_LINES?.map((line) => (
                <a
                  key={line?.name}
                  href={line?.href}
                  target={line?.href?.startsWith('http') ? '_blank' : undefined}
                  rel={line?.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="flex items-center gap-3 p-3.5 rounded-xl bg-muted/40 border border-border hover:bg-muted/60 transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {line?.href?.startsWith('http') ? (
                      <Globe size={16} className="text-primary" />
                    ) : (
                      <Phone size={16} className="text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground font-medium">{line?.country}</p>
                    <p className="text-sm font-bold text-foreground">{line?.name}</p>
                    <p className="text-xs text-primary font-semibold">{line?.contact}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-base font-extrabold text-foreground mb-2">When to Seek Professional Help</h2>
            <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground">
              <li>Thoughts of harming yourself or others</li>
              <li>Persistent feelings of hopelessness lasting weeks</li>
              <li>Difficulty functioning in daily life</li>
              <li>Substance use as a coping mechanism</li>
              <li>Symptoms that feel beyond what Clingy can support</li>
            </ul>
            <p className="text-sm text-foreground mt-3 leading-relaxed">
              Seeking help is a sign of strength. A therapist or counsellor can offer support that goes far beyond what any app can provide.
            </p>
          </div>

          <div>
            <h2 className="text-base font-extrabold text-foreground mb-2">Report a Safety Concern</h2>
            <p className="text-sm text-foreground leading-relaxed">
              If you have a safety concern about the app or another user, please contact us at{' '}
              <a href="mailto:safety@mylittleclingy.com" className="text-primary hover:underline font-semibold">
                safety@mylittleclingy.com
              </a>
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link href="/privacy-policy" className="text-xs text-primary font-semibold hover:underline">Privacy Policy</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/terms" className="text-xs text-primary font-semibold hover:underline">Terms</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/contact" className="text-xs text-primary font-semibold hover:underline">Contact</Link>
        </div>
      </div>
    </div>
  );
}
